/* ============================================================
   DracoSlides Product Quiz — quiz.js
   Scoring engine + UI controller + Klaviyo + PostScript
   No external dependencies. Vanilla JS.
   ============================================================ */

'use strict';

// ── Q2 Style tile images ──────────────────────────────────────
// SWAP: Replace placeholder URLs with your Shopify CDN URLs after
// uploading the 4 AI-generated outfit images to Shopify → Files.
// See INSTALL.md for image generation prompts.
const QUIZ_STYLE_IMAGES = {
  minimal: 'https://cdn.shopify.com/s/files/1/0368/7871/2964/files/openart-image_1777302666792_38527841_1777302666849_cc0f5831.png?v=1777302834',   // SWAP: 'https://cdn.shopify.com/s/files/1/0368/7871/2964/files/openart-image_1777302666792_38527841_1777302666849_cc0f5831.png?v=1777302834'
  street:  'https://cdn.shopify.com/s/files/1/0368/7871/2964/files/1.png?v=1777301190',   // SWAP: 'https://cdn.shopify.com/s/files/1/0368/7871/2964/files/1.png?v=1777301190'
  bold:    'https://cdn.shopify.com/s/files/1/0368/7871/2964/files/3_14f8adfb-b29f-4f3d-ad78-bbefd13cf8c8.png?v=1777301190',   // SWAP: 'https://cdn.shopify.com/s/files/1/0368/7871/2964/files/3_14f8adfb-b29f-4f3d-ad78-bbefd13cf8c8.png?v=1777301190'
  dark:    'https://cdn.shopify.com/s/files/1/0368/7871/2964/files/2_16da3465-4950-4b82-a239-1a179da58905.png?v=1777301187',   // SWAP: 'https://cdn.shopify.com/s/files/1/0368/7871/2964/files/2_16da3465-4950-4b82-a239-1a179da58905.png?v=1777301187'
};

// ── State ─────────────────────────────────────────────────────
const QuizState = {
  answers: { q1: null, q2: null, q3: null, q4: null },
  currentStep: 1,
  totalSteps: 4,
  result: null,
  email: null,
  quizSource: 'shopify_page',
};

// ── Scoring Engine ────────────────────────────────────────────

function getBudgetPick(q3, q1, q2) {
  const P = window.QUIZ_PRODUCTS;
  // All Black always → Blackout V2, regardless of Q3
  if (q2 === 'dark') return P.blackout;
  if (q3 === 'comfort') {
    // Beach/outdoor overrides comfort pick
    return (q1 === 'beach') ? P.reflective : P.original;
  }
  if (q3 === 'durability') return P.reflective;
  if (q3 === 'style')      return P.reflective;
  return P.original; // fallback
}

function getQualityPick(q3, q2) {
  const P = window.QUIZ_PRODUCTS;
  // All Black always → Black Phantom, beats everything including Q3=Style
  if (q2 === 'dark') return P.black_phantom;
  // Style priority gets the collectible
  if (q3 === 'style')  return P.collectible_hero;
  // Bold aesthetic gets the collectible
  if (q2 === 'bold') return P.collectible_hero;
  // Everything else gets Remastered
  return P.remastered;
}

function buildResultCards(budgetPick, qualityPick, q4) {
  const P = window.QUIZ_PRODUCTS;

  let card1, card2, tier;

  if (q4 === 'deal') {
    tier  = 'deal';
    card1 = {
      product:  budgetPick,
      label:    'Save $55 + 50% OFF For Life',
      pitch:    `${budgetPick.title} is your match. Pair it with Draco+ and get 50% off every future order, forever.`,
      addOn:    P.draco_plus,
      addOnText: 'Includes Draco+ trial. 50% off all future pairs.',
      cta:      `Cop ${budgetPick.short_title || budgetPick.title} & Try Draco+`,
      ctaUrl:   budgetPick.url,
    };
    card2 = {
      product:  P.mystery_bogo,
      label:    'Buy 1 Get 1 Free + 50% OFF For Life',
      pitch:    'Get 2 mystery pairs for the price of 1 ($99 total) and add Draco+ for 50% off everything after.',
      addOn:    P.draco_plus,
      addOnText: 'Includes Draco+ trial. 50% off all future pairs.',
      cta:      'Get 2 Pairs & Try Draco+',
      ctaUrl:   P.mystery_bogo.url,
    };
  }

  else if (q4 === 'middle') {
    tier  = 'middle';
    card1 = {
      product:  budgetPick,
      label:    'The Slides Meant For You',
      pitch:    `${budgetPick.short_title || budgetPick.title} is your match. Add Draco+ and your next pair costs 50% less, forever.`,
      addOn:    P.draco_plus,
      addOnText: 'Draco+ offer: 50% off every future pair.',
      cta:      `Cop ${budgetPick.short_title || budgetPick.title} & Try Draco+`,
      ctaUrl:   budgetPick.url,
    };
    card2 = {
      product:  qualityPick,
      label:    'Level Up',
      pitch:    (() => {
        // Compute price delta between the two picks; prices are strings like "$55".
        const toNum = (s) => {
          const n = parseFloat(String(s || '').replace(/[^0-9.]/g, ''));
          return Number.isFinite(n) ? n : null;
        };
        const a = toNum(budgetPick && budgetPick.price);
        const b = toNum(qualityPick && qualityPick.price);
        const name = qualityPick.short_title || qualityPick.title;
        if (a != null && b != null && b > a) {
          const delta = b - a;
          // Strip trailing .00 so "$20" not "$20.00"
          const fmt = '$' + (delta % 1 === 0 ? delta.toFixed(0) : delta.toFixed(2));
          return `For an extra ${fmt}, ${name} takes everything you wanted and levels it up.`;
        }
        return `${name} takes everything you wanted and levels it up.`;
      })(),
      addOn:    P.draco_plus,
      addOnText: 'Draco+ offer: 50% off every future pair.',
      cta:      `Upgrade to ${qualityPick.short_title || qualityPick.title} & Try Draco+`,
      ctaUrl:   qualityPick.url,
    };
  }

  else { // quality
    tier = 'quality';
    const qualityIsRemastered = (qualityPick.handle === 'remastered-dracoslides');
    card1 = {
      product:  qualityPick,
      label:    qualityIsRemastered ? 'Your Match. Worth Every Dollar.' : 'Your Match',
      pitch:    qualityIsRemastered
        ? 'Our most refined pair. Elevated materials, same iconic comfort & fit.'
        : 'Limited run. Statement pair. Built for people who don\'t blend in.',
      addOn:    null,
      cta:      `Shop ${qualityPick.short_title || qualityPick.title}`,
      ctaUrl:   qualityPick.url,
    };
    card2 = {
      product:  qualityIsRemastered ? budgetPick : P.remastered,
      label:    qualityIsRemastered ? 'The Classic' : 'The Safe Bet',
      pitch:    qualityIsRemastered
        ? 'If you want to start here, still the most comfortable slide we make.'
        : 'Our most refined pair. Elevated materials, same iconic comfort & fit.',
      addOn:    null,
      cta:      `Shop ${qualityIsRemastered ? (budgetPick.short_title || budgetPick.title) : 'Remastered'}`,
      ctaUrl:   qualityIsRemastered ? budgetPick.url : P.remastered.url,
    };
  }

  // Trust bar — always shown on results.
  // Outline SVG icons (1.25em, currentColor). No emoji.
  const ICON_TRUCK   = '<svg class="quiz-q4-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 7h11v9H3z"/><path d="M14 10h4l3 3v3h-7"/><circle cx="7" cy="18" r="1.8"/><circle cx="17" cy="18" r="1.8"/></svg>';
  const ICON_SHIELD  = '<svg class="quiz-q4-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3l8 3v6c0 4.5-3.4 8.3-8 9-4.6-.7-8-4.5-8-9V6l8-3z"/><path d="M9 12l2 2 4-4"/></svg>';
  const ICON_RETURN  = '<svg class="quiz-q4-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 12a8 8 0 1 1-2.34-5.66"/><path d="M20 4v4h-4"/></svg>';

  const TRUST_BAR =
    '<span class="quiz-q4-item">' + ICON_TRUCK  + '<span>Free Shipping</span></span>' +
    '<span class="quiz-q4-item">' + ICON_SHIELD + '<span>30 Day Money Back</span></span>' +
    '<span class="quiz-q4-item">' + ICON_RETURN + '<span>Free Returns</span></span>';

  // Trust bar shown on every result. No returning-customer variant.
  const q4Message = TRUST_BAR;

  // Per-tier results page title. Deal tier leans into the savings angle.
  const title = tier === 'deal'
    ? 'We found your slides at the best deal.'
    : 'We found your slides.';

  return { card1, card2, tier, q4Message, title };
}

function getRecommendation() {
  const { q1, q2, q3, q4 } = QuizState.answers;
  const budget  = getBudgetPick(q3, q1, q2);
  const quality = getQualityPick(q3, q2);
  return buildResultCards(budget, quality, q4);
}

// ── UI Controller ─────────────────────────────────────────────

function goToStep(stepNum, reverse = false) {
  const allSteps = document.querySelectorAll('.quiz-step');
  const current  = document.querySelector('.quiz-step.is-active');
  const next     = document.querySelector(`.quiz-step[data-step="${stepNum}"]`);

  if (!next) return;

  if (current) {
    current.classList.add('is-exiting');
    current.addEventListener('animationend', () => {
      current.classList.remove('is-active', 'is-exiting');
    }, { once: true });
  }

  if (reverse) next.classList.add('is-reversing');
  next.classList.add('is-active');
  next.addEventListener('animationend', () => {
    next.classList.remove('is-reversing');
  }, { once: true });

  QuizState.currentStep = stepNum;
  updateProgressBar(stepNum);
}

function updateProgressBar(step) {
  const fill  = document.querySelector('.quiz-progress-fill');
  const label = document.querySelector('.quiz-progress-label');
  if (fill)  fill.style.width = `${(step / QuizState.totalSteps) * 100}%`;
  if (label) {
    label.textContent = `Question ${step} of ${QuizState.totalSteps}`;
    label.style.display = '';
  }
}

function selectAnswer(step, value, tileEl) {
  // Deselect all tiles in this step
  const stepEl = document.querySelector(`.quiz-step[data-step="${step}"]`);
  stepEl.querySelectorAll('.quiz-tile, .quiz-tile-visual').forEach(t => {
    t.classList.remove('is-selected');
    t.setAttribute('aria-selected', 'false');
  });

  // Select this tile
  tileEl.classList.add('is-selected');
  tileEl.setAttribute('aria-selected', 'true');

  // Store answer
  QuizState.answers[`q${step}`] = value;

  // Auto-advance after brief delay
  setTimeout(() => {
    if (step < QuizState.totalSteps) {
      goToStep(step + 1);
    } else {
      showLoading();
    }
  }, 320);
}

function goBack() {
  if (QuizState.currentStep <= 1) return;
  goToStep(QuizState.currentStep - 1, true);
}

function showLoading() {
  const questionsEl = document.querySelector('#quiz-questions');
  const loadingEl   = document.querySelector('#quiz-loading');
  if (questionsEl) questionsEl.style.display = 'none';
  if (loadingEl)   loadingEl.classList.add('is-active');

  const pLabel = document.querySelector('.quiz-progress-label');
  if (pLabel) pLabel.textContent = 'Finding your match…';

  setTimeout(() => {
    renderResults();
  }, 1500);
}

function renderResults() {
  const result = getRecommendation();
  QuizState.result = result;

  const loadingEl = document.querySelector('#quiz-loading');
  const resultsEl = document.querySelector('#quiz-results');
  const fill      = document.querySelector('.quiz-progress-fill');
  const pLabel    = document.querySelector('.quiz-progress-label');

  if (loadingEl) loadingEl.classList.remove('is-active');
  if (fill)      fill.style.width = '100%';
  if (pLabel)    pLabel.style.display = 'none';

  // Per-tier results title
  const titleEl = document.querySelector('.quiz-results-title');
  if (titleEl && result.title) titleEl.textContent = result.title;

  // Q4 trust bar
  const q4El = document.querySelector('#quiz-q4-message');
  if (q4El) {
    if (result.q4Message) {
      q4El.innerHTML = result.q4Message;
      q4El.style.display = '';   // fall back to the .quiz-q4-message flex layout
    } else {
      q4El.style.display = 'none';
    }
  }

  // Render cards
  renderCard('quiz-card-1', result.card1, true);
  renderCard('quiz-card-2', result.card2, false);

  if (resultsEl) resultsEl.classList.add('is-active');

  // SMS prompt removed per product decision.

  // Email slide-up removed — we no longer prompt for email on results.
}

function renderCard(cardId, cardData, isPrimary) {
  const cardEl = document.querySelector(`#${cardId}`);
  if (!cardEl || !cardData) return;

  const p = cardData.product;

  // Set primary modifier
  if (isPrimary) cardEl.classList.add('quiz-card--primary');

  // Label
  const labelEl = cardEl.querySelector('.quiz-card-label');
  if (labelEl) labelEl.textContent = cardData.label;

  // Image
  const imgEl = cardEl.querySelector('.quiz-card-image');
  if (imgEl) {
    imgEl.src = p.image || '';
    imgEl.alt = p.title || '';
    if (!p.image) imgEl.style.background = '#F5F5F5';
  }

  // Name
  const nameEl = cardEl.querySelector('.quiz-card-name');
  if (nameEl) nameEl.textContent = p.title;

  // Price
  const priceEl = cardEl.querySelector('.quiz-card-price');
  if (priceEl) priceEl.textContent = p.price;

  // Pitch
  const pitchEl = cardEl.querySelector('.quiz-card-pitch');
  if (pitchEl) pitchEl.textContent = cardData.pitch;

  // Add-on callout
  const addonEl = cardEl.querySelector('.quiz-card-addon');
  if (addonEl) {
    if (cardData.addOn && cardData.addOnText) {
      addonEl.textContent = cardData.addOnText;
      addonEl.style.display = 'block';
    } else {
      addonEl.style.display = 'none';
    }
  }

  // CTA button
  const ctaEl = cardEl.querySelector('.quiz-card-cta');
  if (ctaEl) {
    ctaEl.textContent = cardData.cta;
    ctaEl.href = cardData.ctaUrl;

    // Track CTA click to Klaviyo if email was captured
    ctaEl.addEventListener('click', () => {
      trackCardClick(cardData, isPrimary ? 'primary' : 'secondary');
    });
  }
}

// ── Klaviyo Integration ───────────────────────────────────────

async function sendToKlaviyo(email) {
  const companyIdEl = document.querySelector('[data-quiz-klaviyo-id]');
  const companyId = companyIdEl ? companyIdEl.dataset.quizKlaviyoId : '';

  if (!companyId || companyId === 'YOUR_KLAVIYO_COMPANY_ID') {
    console.warn('Quiz: Klaviyo Company ID not set. Add it in Shopify theme editor → Quiz section settings.');
    return;
  }

  const { q1, q2, q3, q4 } = QuizState.answers;
  const result = QuizState.result;

  const payload = {
    quiz_q1_use:                q1,
    quiz_q2_style:              q2,
    quiz_q3_priority:           q3,
    quiz_q4_budget:             q4,
    quiz_result_budget_pick:    result && result.card1 ? result.card1.product.title : '',
    quiz_result_quality_pick:   result && result.card2 ? result.card2.product.title : '',
    quiz_tier:                  result ? result.tier : '',
    quiz_source:                QuizState.quizSource,
  };

  try {
    await fetch(`https://a.klaviyo.com/client/events/?company_id=${companyId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'revision': '2023-12-15',
      },
      body: JSON.stringify({
        data: {
          type: 'event',
          attributes: {
            properties: payload,
            metric: {
              data: {
                type: 'metric',
                attributes: { name: 'Quiz Completed' },
              },
            },
            profile: {
              data: {
                type: 'profile',
                attributes: { email },
              },
            },
          },
        },
      }),
    });
  } catch (e) {
    console.warn('Quiz: Klaviyo event failed:', e);
  }
}

function trackCardClick(cardData, position) {
  if (!QuizState.email) return;
  const companyIdEl = document.querySelector('[data-quiz-klaviyo-id]');
  const companyId = companyIdEl ? companyIdEl.dataset.quizKlaviyoId : '';
  if (!companyId || companyId === 'YOUR_KLAVIYO_COMPANY_ID') return;

  fetch(`https://a.klaviyo.com/client/events/?company_id=${companyId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'revision': '2023-12-15' },
    body: JSON.stringify({
      data: {
        type: 'event',
        attributes: {
          properties: {
            quiz_cta_clicked: cardData.label,
            quiz_cta_position: position,
            quiz_product:      cardData.product.title,
            quiz_tier:         QuizState.result ? QuizState.result.tier : '',
          },
          metric: { data: { type: 'metric', attributes: { name: 'Quiz CTA Clicked' } } },
          profile: { data: { type: 'profile', attributes: { email: QuizState.email } } },
        },
      },
    }),
  }).catch(() => {});
}

// ── PostScript Integration ────────────────────────────────────

function showPostScriptPopup() {
  if (window.postscript && window.postscript.popups) {
    window.postscript.popups.find().then(popupId => {
      if (popupId) {
        window.postscript.popups.open(popupId, {
          activePopupBehavior: 'ALWAYS_DISMISS',
          respectPopupStatus: false,
        });
      }
    }).catch(() => {});
  }
}

// ── Email form handler ────────────────────────────────────────

function handleEmailSubmit(email) {
  QuizState.email = email;

  // Hide the slide-up
  const emailSlide = document.querySelector('#quiz-email-slide');
  if (emailSlide) emailSlide.classList.remove('is-visible');

  // Send to Klaviyo
  sendToKlaviyo(email).catch(() => {});
}

// ── Reset / restart ───────────────────────────────────────────

function restartQuiz() {
  QuizState.answers = { q1: null, q2: null, q3: null, q4: null };
  QuizState.currentStep = 1;
  QuizState.result = null;
  QuizState.email = null;

  const questionsEl = document.querySelector('#quiz-questions');
  const loadingEl   = document.querySelector('#quiz-loading');
  const resultsEl   = document.querySelector('#quiz-results');
  const emailSlide  = document.querySelector('#quiz-email-slide');
  const smsEl       = document.querySelector('#quiz-sms-prompt');

  if (questionsEl) questionsEl.style.display = '';
  if (loadingEl)   loadingEl.classList.remove('is-active');
  if (resultsEl)   resultsEl.classList.remove('is-active');
  if (emailSlide)  emailSlide.classList.remove('is-visible');
  if (smsEl)       smsEl.classList.remove('is-visible');

  // Reset all tile selections
  document.querySelectorAll('.quiz-tile, .quiz-tile-visual').forEach(t => {
    t.classList.remove('is-selected');
    t.setAttribute('aria-selected', 'false');
  });

  // Reset cards
  document.querySelectorAll('.quiz-card').forEach(c => c.classList.remove('quiz-card--primary'));

  goToStep(1);
}

// ── Inject Q2 images when ready ───────────────────────────────

function injectStyleImages() {
  Object.entries(QUIZ_STYLE_IMAGES).forEach(([style, url]) => {
    if (!url) return;
    const tile = document.querySelector(`.quiz-tile-visual[data-style="${style}"]`);
    if (!tile) return;
    const img = document.createElement('img');
    img.src   = url;
    img.alt   = style;
    img.className = 'quiz-tile-visual-img';
    // Insert before the label
    const label = tile.querySelector('.quiz-tile-visual-label');
    tile.insertBefore(img, label);
  });
}

// ── Bootstrap ─────────────────────────────────────────────────

function initQuiz() {
  // Read quiz source from data attribute
  const wrapper = document.querySelector('[data-quiz-source]');
  if (wrapper) QuizState.quizSource = wrapper.dataset.quizSource || 'shopify_page';

  // Read URL param override (e.g. ?quiz_source=nb_e2_mv)
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('quiz_source')) {
    QuizState.quizSource = urlParams.get('quiz_source');
  }

  // Wire up answer tiles
  document.querySelectorAll('[data-quiz-answer]').forEach(tile => {
    tile.addEventListener('click', () => {
      const step  = parseInt(tile.closest('[data-step]').dataset.step, 10);
      const value = tile.dataset.quizAnswer;
      selectAnswer(step, value, tile);
    });
    tile.addEventListener('keydown', e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        tile.click();
      }
    });
  });

  // Wire up back buttons
  document.querySelectorAll('[data-quiz-back]').forEach(btn => {
    btn.addEventListener('click', goBack);
  });

  // Wire up restart
  const restartBtn = document.querySelector('#quiz-restart');
  if (restartBtn) restartBtn.addEventListener('click', restartQuiz);

  // Wire up email form
  const emailForm = document.querySelector('#quiz-email-form');
  if (emailForm) {
    emailForm.addEventListener('submit', e => {
      e.preventDefault();
      const input = emailForm.querySelector('.quiz-email-input');
      const email = input ? input.value.trim() : '';
      if (email && email.includes('@')) {
        handleEmailSubmit(email);
      }
    });
  }

  // Wire up email dismiss
  const emailDismiss = document.querySelector('#quiz-email-dismiss');
  if (emailDismiss) {
    emailDismiss.addEventListener('click', () => {
      const emailSlide = document.querySelector('#quiz-email-slide');
      if (emailSlide) emailSlide.classList.remove('is-visible');
    });
  }

  // SMS button removed per product decision.

  // Inject Q2 style images
  injectStyleImages();

  // Init progress bar
  updateProgressBar(1);
}

// ── Run on DOM ready ──────────────────────────────────────────
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initQuiz);
} else {
  initQuiz();
}
